import { PLATFORM_STATISTIC, RELEASE_NOISE_FLOOR, SR_ALLOCATIONS_STAGE, srStagesFor } from './constants';
import { formatMetric, formatScenarioLabel, displayDeviceLabel } from './format';
import { isMissing, releaseVerdictOf, scenarioFigures, srStageValue } from './metrics';

const VERDICT_CLASS = { above: 'sdkp-delta-worse', below: 'sdkp-delta-better', steady: 'sdkp-delta-flat' };

const FIGURE_COLUMNS = {
  cpu: { label: 'CPU', rawUnit: 'percent', costUnit: 'points' },
  total_memory: { label: 'Memory', rawUnit: 'mb', costUnit: 'mb' },
  fps: { label: 'FPS', rawUnit: 'fps', costUnit: 'fps' },
};

function signedChange(value, previous, unit) {
  if (isMissing(value) || isMissing(previous)) return '-';
  const change = value - previous;
  const percent = previous ? ` (${change > 0 ? '+' : ''}${Math.round((change / Math.abs(previous)) * 100)} %)` : '';
  return `${formatMetric(change, unit, true)}${percent}`;
}

function stagesOf(platform) {
  return [...srStagesFor(platform), ...(platform === 'android' ? [SR_ALLOCATIONS_STAGE] : [])];
}

// `current`, `previous`: one run's metrics through the page filters; no
// `previous` means there is no earlier release to compare with.
export function SessionReplayStagesTable({ platform, current, previous, previousLabel }) {
  const rows = stagesOf(platform)
    .map((stage) => ({
      stage,
      value: srStageValue(current, stage.metric),
      previous: previous ? srStageValue(previous, stage.metric) : null,
    }))
    .filter((row) => !isMissing(row.value));
  const releaseMode = Boolean(previous);
  return (
    <div className="sdkp-panel sdkp-section-spaced" data-panel="release-sr-stages">
      <h2 className="sdkp-panel-title">Session Replay stages</h2>
      <p className="sdkp-panel-hint">Average per capture, on the slowest device when several are shown.</p>
      {!rows.length && <div className="sdkp-empty">No Session Replay stage timings in this run for the selected filters.</div>}
      {rows.length > 0 && (
        <table className="sdkp-table">
          <thead>
            <tr>
              <th>Stage</th>
              <th>Unit</th>
              <th className="sdkp-num">This release</th>
              {releaseMode && (
                <>
                  <th className="sdkp-num">{previousLabel}</th>
                  <th className="sdkp-num">Change</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const unit = row.stage.unit || 'ms';
              const verdict = releaseVerdictOf(row.value, row.previous, 0);
              return (
                <tr key={row.stage.metric}>
                  <td>{row.stage.label}</td>
                  <td>{unit === 'count' ? 'count / capture' : 'ms / capture'}</td>
                  <td className="sdkp-num">{formatMetric(row.value, unit === 'count' ? null : unit)}</td>
                  {releaseMode && (
                    <>
                      <td className="sdkp-num">{formatMetric(row.previous, unit === 'count' ? null : unit)}</td>
                      <td className={`sdkp-num ${VERDICT_CLASS[verdict] || ''}`}>{signedChange(row.value, row.previous, unit === 'count' ? null : unit)}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

const rowKey = (row) => `${row.scenario}|${row.device}`;

// One row per scenario and device: with SDK / without SDK and the cost, per
// figure, and the cost change against the previous release when there is one.
export function ScenarioTable({ platform, current, previous, previousLabel, hint }) {
  const names = Object.keys(PLATFORM_STATISTIC[platform] || {});
  const rows = scenarioFigures(current, platform);
  const releaseMode = Boolean(previous);
  const previousRows = new Map(previous ? scenarioFigures(previous, platform).map((row) => [rowKey(row), row]) : []);
  return (
    <div className="sdkp-panel sdkp-section-spaced" data-panel="release-scenarios">
      <h2 className="sdkp-panel-title">Scenarios</h2>
      <p className="sdkp-panel-hint">{hint} Cost = with SDK − without SDK (FPS: without − with).</p>
      {!rows.length && <div className="sdkp-empty">No scenario figures in this run for the selected filters.</div>}
      {rows.length > 0 && (
        <div className="sdkp-table-scroll">
          <table className="sdkp-table">
            <thead>
              <tr>
                <th>Scenario</th>
                <th>Device</th>
                {names.map((name) => (
                  <th key={name} colSpan={releaseMode ? 3 : 2} className="sdkp-num">{FIGURE_COLUMNS[name].label}</th>
                ))}
              </tr>
              <tr>
                <th aria-label="Scenario" />
                <th aria-label="Device" />
                {names.flatMap((name) => [
                  <th key={`${name}-raw`} className="sdkp-num">with / without</th>,
                  <th key={`${name}-cost`} className="sdkp-num">cost</th>,
                  ...(releaseMode ? [<th key={`${name}-change`} className="sdkp-num">vs {previousLabel}</th>] : []),
                ])}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const before = previousRows.get(rowKey(row));
                return (
                  <tr key={rowKey(row)}>
                    <td>{formatScenarioLabel(row.scenario) || row.scenario}</td>
                    <td>{displayDeviceLabel(row.device)}</td>
                    {names.flatMap((name) => {
                      const figure = row.figures[name];
                      const { rawUnit, costUnit } = FIGURE_COLUMNS[name];
                      const previousCost = before?.figures?.[name]?.overhead ?? null;
                      const verdict = releaseVerdictOf(figure.overhead, previousCost, RELEASE_NOISE_FLOOR[name]);
                      return [
                        <td key={`${name}-raw`} className="sdkp-num">{`${formatMetric(figure.enabled, rawUnit)} / ${formatMetric(figure.disabled, rawUnit)}`}</td>,
                        <td key={`${name}-cost`} className="sdkp-num"><b>{formatMetric(figure.overhead, costUnit, true)}</b></td>,
                        ...(releaseMode ? [
                          <td key={`${name}-change`} className={`sdkp-num ${VERDICT_CLASS[verdict] || 'sdkp-delta-flat'}`}>
                            {isMissing(previousCost) || isMissing(figure.overhead) ? '-' : formatMetric(figure.overhead - previousCost, costUnit, true)}
                          </td>,
                        ] : []),
                      ];
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
